(() => {
  'use strict';
  // A section can reload in the theme editor without reloading the document.
  if (window.JCLGLArticle) { window.JCLGLArticle.mount(document); return; }
  const instances = new Map();

  function init(root) {
    if (instances.has(root)) return;
    const abort = new AbortController();
    const { signal } = abort;
    const body = root.querySelector('[data-article-body]');
    if (!body) return;
    const tableObservers = [];
    root.querySelectorAll('[data-copy-article]').forEach(button => {
      button.hidden = false;
      button.addEventListener('click', async () => {
        const box = button.closest('.jca__socials');
        const status = box.querySelector('[data-copy-status]');
        const fallback = box.querySelector('.jca__copy-fallback');
        try {
          if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
          await navigator.clipboard.writeText(button.dataset.copyArticle);
          if (signal.aborted) return;
          status.textContent = 'Link copied.';
          fallback.hidden = true;
        } catch (_) {
          if (signal.aborted) return;
          fallback.hidden = false;
          fallback.focus();
          fallback.select();
          status.textContent = 'Select and copy the article link below.';
        }
      }, { signal });
    });
    root.querySelectorAll('[data-related]').forEach(box => {
      const track = box.querySelector('.jca__related-track');
      const controls = box.querySelector('[data-related-controls]');
      const prev = box.querySelector('[data-related-prev]');
      const next = box.querySelector('[data-related-next]');
      const update = () => {
        const overflow = track.scrollWidth - track.clientWidth;
        controls.hidden = overflow < 2;
        prev.disabled = track.scrollLeft < 2;
        next.disabled = track.scrollLeft >= overflow - 2;
        track.tabIndex = overflow < 2 ? -1 : 0;
      };
      for (const [button, direction] of [[prev, -1], [next, 1]]) {
        button.addEventListener('click', () => {
          const amount = track.firstElementChild.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 20);
          track.scrollBy({left: direction * amount, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
        }, { signal });
      }
      track.addEventListener('scroll', update, { passive: true, signal });
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(update); observer.observe(track); tableObservers.push(observer);
      }
      update();
    });
    const sidebar = root.querySelector('.jca__sidebar');
    if (sidebar && 'ResizeObserver' in window) {
      const sidebarObserver = new ResizeObserver(() => {
        root.style.setProperty('--jca-sidebar-height', `${Math.ceil(sidebar.getBoundingClientRect().height)}px`);
      });
      sidebarObserver.observe(sidebar);
      tableObservers.push(sidebarObserver);
    }
    body.querySelectorAll('table').forEach((table, index) => {
      if (table.parentElement.classList.contains('jca__table-scroll')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'jca__table-scroll';
      wrapper.setAttribute('role', 'region');
      wrapper.setAttribute('aria-label', table.caption?.textContent.trim() || `${root.dataset.tableLabel || 'Article table'} ${index + 1}`);
      table.before(wrapper);
      wrapper.append(table);
      const update = () => { wrapper.tabIndex = wrapper.scrollWidth > wrapper.clientWidth + 1 ? 0 : -1; };
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(update);
        observer.observe(wrapper);
        tableObservers.push(observer);
      }
      update();
    });

    const toc = root.querySelector('[data-article-toc]');
    const list = toc?.querySelector('ol');
    const details = toc?.querySelector('details');
    const selector = root.dataset.tocDepth === 'h2-h3' ? 'h2, h3' : 'h2';
    const headings = Array.from(body.querySelectorAll(selector)).filter(h => h.textContent.trim() && !h.closest('[data-toc-exclude]'));
    const links = [];
    if (toc && list && headings.length) {
      list.replaceChildren();
      headings.forEach((heading, index) => {
        if (!heading.id || document.getElementById(heading.id) !== heading) {
          const stem = heading.textContent.trim().toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '') || `section-${index + 1}`;
          let id = stem;
          let n = 2;
          while (document.getElementById(id)) id = `${stem}-${n++}`;
          heading.id = id;
        }
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = '#' + encodeURIComponent(heading.id);
        a.textContent = heading.textContent.trim();
        a.dataset.level = heading.tagName.slice(1);
        a.addEventListener('click', event => {
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          if (window.matchMedia('(max-width: 989px)').matches) details.open = false;
          heading.tabIndex = -1;
          heading.focus({ preventScroll: true });
          heading.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
          history.replaceState(null, '', a.hash);
          mark(index);
        }, { signal });
        li.append(a);
        list.append(li);
        links.push(a);
      });
      toc.hidden = false;
      const media = window.matchMedia('(max-width: 989px)');
      const setOpen = () => { details.open = !media.matches; };
      setOpen();
      media.addEventListener('change', setOpen, { signal });
    }
    if ((!toc || toc.hidden) && !root.querySelector('[data-newsletter]')) root.classList.add('jca--no-sidebar');
    else root.classList.remove('jca--no-sidebar');

    let active = -1;
    function mark(index) {
      if (active === index) return;
      links.forEach((link, i) => {
        if (i === index) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      active = index;
    }
    let frame = 0;
    const updateActive = () => {
      frame = 0;
      if (!links.length) return;
      const offset = parseFloat(getComputedStyle(root).getPropertyValue('--jca-offset')) || 120;
      let index = 0;
      headings.forEach((heading, i) => { if (heading.getBoundingClientRect().top <= offset + 28) index = i; });
      mark(index);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(updateActive); };
    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    updateActive();
    // Generated anchors also work when the article is opened with a deep link.
    if (location.hash) {
      let target;
      try { target = document.getElementById(decodeURIComponent(location.hash.slice(1))); } catch (_) { /* Malformed external fragment. */ }
      if (target && body.contains(target)) requestAnimationFrame(() => target.scrollIntoView({ block: 'start', behavior: 'auto' }));
    }
    instances.set(root, () => {
      abort.abort();
      cancelAnimationFrame(frame);
      tableObservers.forEach(observer => observer.disconnect());
    });
  }

  function mount(scope) {
    if (scope.matches?.('[data-jclgl-article]')) init(scope);
    scope.querySelectorAll('[data-jclgl-article]').forEach(init);
  }
  window.JCLGLArticle = { mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((cleanup, root) => { if (event.target.contains(root)) { cleanup(); instances.delete(root); } });
  });
})();
